import * as React from 'react';
import type { Theme } from '@mui/system';
import { styled } from '@mui/system';
import { alpha } from '@mui/material';
import { Container, Item } from 'Components/Grid';
import { fetchWithOptions } from 'Utility/fetch';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { useIntl } from 'react-intl';
import { Key, PlayArrow } from '@mui/icons-material';
import OTPDialog from 'Routes/MyService/Dash/OTPDialog';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import colors from '../../../../../config/theme/colors';
import { take } from 'Utility/ramdaReplacement';
import { useAppSelector } from 'Store/index';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { hasAttributes, isJSObjectType, isNumber, partialDateToValue } from 'neuro-utils';
import { filterDocumentsByType } from 'Utility/documentHandling';

const TileHeader = styled(Item)(({ theme }: { theme?: Theme }) => ({
  height: '5rem',
  color: theme?.customPalette.myms.primary.main,
  cursor: 'default',
}));

const TileFooter = styled(Item)(({ theme }: { theme?: Theme }) => ({
  backgroundColor: theme?.customPalette.myms.primary.main && alpha(theme.customPalette.myms.primary.main, 0.75),
  margin: `0 -2rem`,
  padding: `0 2rem`,
  height: '5rem',
  cursor: 'default',
}));

const HeaderText = styled('div')({
  alignItems: 'center',
  display: 'flex',
  fontWeight: 600,
  fontSize: '1.6rem',
});

const ButtonHeader = styled('div')(({ theme }: { theme?: Theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme?.customPalette.myms.primary.main,
  fontWeight: 600,
  fontSize: '1.4rem',
  cursor: 'pointer',
}));

const ButtonHeaderText = styled('div')(({ theme }: { theme?: Theme }) => ({
  color: theme?.customPalette.myms.primary.main && alpha(theme.customPalette.myms.primary.main, 0.5),
}));

const Button = styled('div')(({ theme }: { theme?: Theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme?.palette.common.white,
  fontWeight: 600,
  fontSize: '1.4rem',
  cursor: 'pointer',
}));

const onClickButtonArea = async (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const onClickButtonHeader = async () => {
  const response = await fetchWithOptions('/api/redirect/myms', { neurojwt: parseJWTFromCookie() }, { method: 'GET' });
  const parsedBody = await response.json();
  window.open(parsedBody.redirectLocation);
};

const MsServiceButtons = ({ OTPDialogOpen, setOTPDialogOpen, otpData, getOTP }: IOTPProps) => {
  const { formatMessage } = useIntl();

  return (
    <React.Fragment>
      <Container alignContent={'center'}>
        <Item xs={6}>
          <Button
            onClick={() => {
              setOTPDialogOpen(true);
              getOTP();
            }}
          >
            <Key style={{ display: 'block', marginRight: '0.7rem' }} />
            {formatMessage({ id: 'myService.createOTP' })}
          </Button>
        </Item>
      </Container>
      <OTPDialog dialogOpen={OTPDialogOpen} setDialogOpen={setOTPDialogOpen} otpData={otpData} />
    </React.Fragment>
  );
};

const HeaderComponent = () => {
  const { formatMessage } = useIntl();
  return (
    <TileHeader onClick={onClickButtonArea}>
      <Container direction="row" alignItems="center" style={{ height: '100%' }}>
        <Item xs={true}>
          <HeaderText>{'StellarQ MyMS'}</HeaderText>
        </Item>
        <Item style={{ width: '13.5rem' }}>
          <ButtonHeader onClick={onClickButtonHeader}>
            <Container justifyContent={'flex-end'}>
              <ButtonHeaderText>{formatMessage({ id: 'myService.viewInfo' })}</ButtonHeaderText>
              <PlayArrow style={{ display: 'block' }} viewBox={'0 0 14 24'} />
            </Container>
          </ButtonHeader>
        </Item>
      </Container>
    </TileHeader>
  );
};

const FooterComponent = ({ OTPDialogOpen, setOTPDialogOpen, otpData, getOTP }: IOTPProps) => (
  <TileFooter onClick={onClickButtonArea}>
    <Container direction={'row'} alignItems="center" style={{ height: '100%' }}>
      <Item style={{ width: '100%' }}>
        <MsServiceButtons {...{ OTPDialogOpen, setOTPDialogOpen, otpData, getOTP }} />
      </Item>
    </Container>
  </TileFooter>
);

type OTP = { code: string; expiration: number };

const getOTP = async (): Promise<OTP | undefined> => {
  try {
    const res = await fetchWithOptions(`/api/otpCreate`, { neurojwt: parseJWTFromCookie() }, { method: 'POST' });
    if (res.status !== 200) throw new Error(`unexpected status ${res.status}`);
    const data = await res.json();
    if (
      isJSObjectType(data) &&
      hasAttributes(data, ['code', 'expires']) &&
      typeof data.code === 'string' &&
      isNumber(data.expires)
    ) {
      return { code: data.code, expiration: data.expires };
    } else throw new Error(`unable to parse otp data`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'create OTP'));
    return undefined;
  }
};

const MyServiceAccess = ({ platform }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const [requestingOTP, setRequestingOTP] = React.useState<boolean>(false);

  // oxlint-disable-next-line react/hook-use-state
  const [OTPDialogOpen, setOTPDialogOpen] = React.useState(false);
  // oxlint-disable-next-line react/hook-use-state
  const [OTPData, setOTPData] = React.useState<OTP | undefined>(undefined);

  const handleRequestOTP = (): void => {
    setRequestingOTP(true);
    getOTP()
      .then(res => {
        if (!res) return; // TODO display some error the the user
        else {
          setOTPDialogOpen(true);
          setOTPData(res);
        }
      })
      .finally(() => setRequestingOTP(false));
  };

  const myDocuments = useAppSelector(s => s.myapp.sortedAndMergedDocuments);
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage ?? 'fi');

  const questionnaires = myDocuments
    .filter(
      d =>
        d._type !== 'relapse' &&
        ['fss', 'msis', 'eq5d', 'fsmc', 'msnq', 'status', 'predss', 'qualityoflife'].includes(d._type),
    )
    .sort((a, b) => b._cdate - a._cdate);

  const relapses = filterDocumentsByType(myDocuments, ['relapseSuspicion']).sort(
    (a, b) => partialDateToValue(b.relapseDate) - partialDateToValue(a.relapseDate),
  );

  const twoMostRecentQuestionnaires = take(2, questionnaires);
  const mostRecentRelapse = take(1, relapses);

  return platform === 'ms' ? (
    <div style={{ border: `1px solid ${colors.gray}`, borderRadius: '0.3rem', padding: '0.5rem 2rem 0 2rem' }}>
      <Container direction={'column'} style={{ height: '100%' }} rowSpacing={'0rem'}>
        <HeaderComponent />
        <Item xs={true}>
          {[...questionnaires, ...relapses].length > 0 ? (
            <div style={{ margin: '-1rem 0 1rem 0' }}>
              <TileContentMaker
                type="listWithSubheaders"
                data={{
                  [formatMessage({ id: 'myService.latestQueries' })]: {
                    type: 'recentList',
                    data: twoMostRecentQuestionnaires.map(d => ({
                      date: new Date(d._cdate).toLocaleDateString(locale),
                      title: formatMessage({ id: 'myService.documents.' + d._type }),
                      key: d._id,
                    })),
                  },
                  [formatMessage({ id: 'myService.relapseSuspicion' })]: {
                    type: 'specificList',
                    data: mostRecentRelapse.map(d => ({
                      date: new Date(partialDateToValue(d.relapseDate)).toLocaleDateString(locale),
                      title: formatMessage({ id: 'myService.documents.' + 'relapse' }),
                      key: d._id,
                    })),
                  },
                }}
              />
            </div>
          ) : (
            <Container style={{ height: '100%' }} justifyContent="center" alignItems="center">
              <Item style={{ fontSize: 24, color: colors.lightGray, marginBottom: '2rem' }}>
                {formatMessage({ id: 'myService.noData' })}
              </Item>
            </Container>
          )}
        </Item>

        <FooterComponent {...{ OTPDialogOpen, setOTPDialogOpen, otpData: OTPData, getOTP }} />
      </Container>
    </div>
  ) : (
    <React.Fragment>
      <ActionButtonRounded
        text={
          <Container alignItems="center">
            <Item xs={true} style={{ paddingLeft: '2.5rem' }}>
              {formatMessage({ id: 'myService.createOTP' })}
            </Item>
            <Item>{<Key style={{ display: 'block', marginRight: '0.7rem' }} />}</Item>
          </Container>
        }
        onClick={handleRequestOTP}
        loading={requestingOTP}
        width={20}
        height={4}
        fontSize={16}
        uppercase={false}
        colorScheme="myms"
        filled
        alternate
      />
      <OTPDialog dialogOpen={OTPDialogOpen} setDialogOpen={setOTPDialogOpen} otpData={OTPData} />
    </React.Fragment>
  );
};

MyServiceAccess.displayName = 'MyServiceAccess';

interface IOTPProps {
  OTPDialogOpen: boolean;
  setOTPDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  otpData:
    | {
        code: string;
        expiration: number;
      }
    | undefined;
  getOTP: () => void;
}

interface IOwnProps {
  platform: Platform | undefined;
}

export default MyServiceAccess;
